/* eslint @typescript-eslint/strict-boolean-expressions: off */

import Link from "next/link";
import styles from "gui/project/project.module.css";
import { Card } from "gui/card/card";
import { CardProps } from "gui/card/card";
import { createProjectTheme } from "pages/projects/[slug]";
import { Grid } from "gui/grid/grid";
import { Icon } from "gui/icon/icon";
import { Project } from "lib/projects/types";
import { queryPath } from "lib/links/links";
import { Subtitle } from "gui/subtitle/subtitle";

export const ProjectGrid: React.FC<{
  project: Project;
  revealOnLoad?: boolean;
  revealOnScroll?: boolean;
}> = (props) => {
  const id = `aria-${props.project.id}`;

  return (
    <Grid
      revealOnLoad={props.revealOnLoad}
      revealOnScroll={props.revealOnScroll}
      aria-labelledby={id}
      style={createProjectTheme(props.project)}
      gaps
    >
      <Subtitle id={id} text={`${props.project.name} on Dune`}>
        <Link href="/projects/[slug]" as={`/projects/${props.project.id}`}>
          <a>
            See project
            <Icon icon="arrow-right" />
          </a>
        </Link>
      </Subtitle>
      <Link href="/projects/[slug]" as={`/projects/${props.project.id}`}>
        <a className={styles.logo}>
          <ProjectLogo project={props.project} />
        </a>
      </Link>
      <div className={styles.description}>
        {props.project.description && (
          <div
            className={styles.html}
            dangerouslySetInnerHTML={{ __html: props.project.description }}
          />
        )}
        <ProjectTags project={props.project} />
      </div>
      {projectCardProps(props.project)
        .slice(0, 2)
        .map((card, i) => (
          <Card key={i} {...card} />
        ))}
    </Grid>
  );
};

export const ProjectLogo: React.FC<{
  project: Project;
}> = (props) => {
  const src = props.project.logo
    ? `/projects/pages/${props.project.id}/${props.project.logo}`
    : `/assets/logo.svg`;

  const alt = props.project.logo ? `${props.project.name} logo` : `Dune logo`;

  return <img src={src} alt={alt} />;
};

export const ProjectTags: React.FC<{
  project: Project;
}> = (props) => {
  if (!props.project.tags?.length) {
    return null;
  }

  return (
    <ul className={styles.tags}>
      {props.project.tags.map((item) => (
        <li key={item}>
          <Link
            href="/browse/[slug]"
            as={`/browse/dashboards?tags=${item}`}
            prefetch={false}
          >
            <a>#{item}</a>
          </Link>
        </li>
      ))}
    </ul>
  );
};

export const ProjectLinks: React.FC<{
  project: Project;
}> = (props) => {
  if (!props.project.links?.length) {
    return null;
  }

  return (
    <ul className={styles.links}>
      {props.project.links.map((item) => (
        <li key={item.name}>
          <a href={item.link}>{item.name}</a>
        </li>
      ))}
    </ul>
  );
};

export const projectCardProps = (project: Project) => {
  const dashboards = project.dashboards ?? [];
  const queries = project.queries ?? [];

  // Format dashboard card item data.
  const dashboardItems: CardProps[] = dashboards.map((item) => ({
    icon: "bar-chart",
    title: item.name,
    subtitle: `Dashboard by @${item.owner.handle}`,
    href: "/[...slug]",
    as: `/${item.owner.handle}/${item.slug}`,
  }));

  // Format query card item data.
  const queryItems: CardProps[] = queries.map((item) => ({
    icon: "terminal",
    title: item.name,
    subtitle: `Query by @${item.owner.handle}`,
    href: "/queries/[...slug]",
    as: queryPath(item.id),
  }));

  return dashboardItems.concat(queryItems);
};
